import React, { useEffect, useState } from "react";
import { TextField, Checkbox, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useFormikContext } from "formik";
import { useOrderAttributeProvider } from "../context/order";
import PhoneNumber from "Components/FormControl/PhoneNumber/PhoneNumber";
import { DEFAULT_COUNTRY_CODE_NAME } from "Helpers/Constants";
import SwitchBox from "../../../../Components/FormControl/SwitchBox/SwitchBox";
import { keepNumbersOnly } from "../utility/util";
import { validateTwilioPhoneNumber } from "api/util";
import CheckBox from "Components/FormControl/CheckBox/CheckBox";


const PROCESSING_STATE = "processing";

// ✅ New styled version of GreenCheckbox
const GreenCheckbox = styled((props) => (
  <Checkbox color="default" {...props} />
))({
  color: "#CB998D",
  "&.Mui-checked": {
    color: "#CB998D",
  },
});

const CustomerInfo = () => {
  const formik = useFormikContext();
  const [overriddenPhoneNumber, setOverriddenPhoneNumber] = useState(false);
  const { module, active, orderDetail } = useOrderAttributeProvider();

  const isDisabledEmail =
    module === "duplicate-order" || module === "replace-order";

  // validate twilio phone number on first load
  useEffect(() => {
    // validate twilio phone number for customer phone
    const validateTwilioCustomerOnFirstLoad = async () => {
      if(formik?.values?.customer_info?.customer_phone && module !== "edit-order"){
      let customer_info = { ...formik.values.customer_info };
      let phoneNumber = keepNumbersOnly(customer_info?.customer_phone)
      let phoneCountryCode = keepNumbersOnly(customer_info?.customer_phone_country_code);
      let is_valid_phone_number = customer_info?.is_valid_phone_number;
      //Prevents multiple API calls for the same phone number
        if(phoneNumber && phoneNumber.length >= 10  && !overriddenPhoneNumber && !is_valid_phone_number){
          // Call API to validate twilio phone number
          const response = await validateTwilioPhoneNumber({  
            phone_number: [{phone:phoneCountryCode+phoneNumber, id:1}]
          });
          let is_valid_phone_number = response?.data?.data?.is_valid;
          // If phone number is not valid, show overridden phone number
          if(!is_valid_phone_number){
            customer_info['is_show_overridden_phone_number'] = true;
            customer_info['is_valid_phone_number'] = is_valid_phone_number;
            formik.setValues((v) => {
              return {
                ...v,
                customer_info: customer_info,
              };
            });
            //This the fallback for the formik values
            if(!formik.values.customer_info.is_show_overridden_phone_number ){
              formik.values.customer_info.is_valid_phone_number = false;
              formik.values.customer_info.is_show_overridden_phone_number = true;
            }
          } else{
            // If phone number is valid, hide overridden phone number
            customer_info['is_show_overridden_phone_number'] = false;
            customer_info['is_valid_phone_number'] = true;
            formik.setValues((v) => {
              return {
                ...v,
                customer_info: customer_info,
              };
            });
            if(!formik.values.customer_info.is_valid_phone_number ){
              formik.values.customer_info.is_valid_phone_number = true;
              formik.values.customer_info.is_show_overridden_phone_number = false;
            }
          }
        }else{
          // Only reset if phone number is too short
          // Don't reset if is_valid_phone_number is already true (from previous API call)
          // The else block runs when: phoneNumber is falsy OR length < 10 OR overriddenPhoneNumber is true OR is_valid_phone_number is already true
          if(!phoneNumber || phoneNumber.length < 10 || !is_valid_phone_number){
            customer_info['is_show_overridden_phone_number'] = false;
            customer_info['is_valid_phone_number'] = false;
            formik.setValues((v) => {
              return {
                ...v,
                customer_info: customer_info,
              };
            });
          }
          setOverriddenPhoneNumber(false);
        }
      }
    }
    validateTwilioCustomerOnFirstLoad();
  }, [formik?.values?.customer_info?.customer_phone]);
  
  // show overridden phone number if is_show_overridden_phone_number is true
  const isShowOverriddenPhoneNumber = formik.values?.customer_info?.is_show_overridden_phone_number || false;
  
  return (
    <div className="row mt-5">
      <div className="col-12">
        <h3 className="bold">Customer Information</h3>
      </div>

      {/* First Name */}
      <div className="my-2 col-md-4">
        <TextField
          value={formik.values.customer_info.customer_first_name}
          id="customer_info.customer_first_name"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={
            formik.touched?.customer_info?.customer_first_name &&
            Boolean(formik.errors?.customer_info?.customer_first_name)
          }
          helperText={
            formik.touched?.customer_info?.customer_first_name &&
            formik.errors?.customer_info?.customer_first_name
          }
          disabled={active !== 0}
          className="w-100"
          label="First Name"
          variant="standard"
        />
      </div>

      {/* Last Name */}
      <div className="my-2 col-md-4">
        <TextField
          value={formik.values.customer_info.customer_last_name}
          id="customer_info.customer_last_name"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={
            formik.touched?.customer_info?.customer_last_name &&
            Boolean(formik.errors?.customer_info?.customer_last_name)
          }
          helperText={
            formik.touched?.customer_info?.customer_last_name &&
            formik.errors?.customer_info?.customer_last_name
          }
          disabled={active !== 0}
          className="w-100"
          label="Last Name"
          variant="standard"
        />
      </div>

      {/* Email */}
      <div className="my-2 col-md-4">
        <TextField
          value={formik.values.customer_info.customer_email}
          id="customer_info.customer_email"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={
            formik.touched?.customer_info?.customer_email &&
            Boolean(formik.errors?.customer_info?.customer_email)
          }
          helperText={
            formik.touched?.customer_info?.customer_email &&
            formik.errors?.customer_info?.customer_email
          }
          disabled={isDisabledEmail || active !== 0}
          className="w-100"
          label="Email"
          type="email"
          variant="standard"
        />
      </div>

      {/* Phone */}
      <div className="my-2 col-md-4">
        <PhoneNumber
          countryCode={DEFAULT_COUNTRY_CODE_NAME}
          initValue={
            formik.values.customer_info?.customer_phone_country_code +
            formik.values.customer_info?.customer_phone
          }
          specialLabel="Phone"
          fieldName="customer_info.customer_phone"
          onChange={async (phone, countryData, event, formatedValue) => {
            const countryCode = "+" + countryData.dialCode;
            const finalMobileNum = formatedValue
              ? formatedValue?.split(countryCode)[1]?.trim()
              : "";
              //Initialize variables
              let customerInfo = {...formik.values.customer_info};
            formik.setValues((v) => ({
              ...v,
              customer_info: {
                ...customerInfo,
                customer_phone: finalMobileNum,
                customer_phone_country_code: countryCode,
              },
            }));
          }}
          disabled={active !== 0}
        />
        {isShowOverriddenPhoneNumber && module !== "edit-order" &&(
          <div style={{ marginTop: "-18px" }}>
            <CheckBox
              value={overriddenPhoneNumber}
              label="Override phone number"
              onChange={(data) => {
                setOverriddenPhoneNumber(data);
                formik.setValues((v) => ({
                    ...v,
                    customer_info: { ...formik.values.customer_info,
                    is_valid_phone_number: data,
                  },
                }));
                formik.values.customer_info.is_valid_phone_number = data;
              }}
            />
         </div>
        )}  
      </div>

      {/* ALT Phone */}
      <div className="my-2 col-md-4">
        <PhoneNumber
          countryCode={DEFAULT_COUNTRY_CODE_NAME}
          initValue={
            formik.values.customer_info?.customer_alt_phone_country_code +
            formik.values.customer_info?.customer_alt_phone
          }
          specialLabel="ALT Phone"
          fieldName="customer_info.customer_alt_phone"
          onChange={(phone, countryData, event, formatedValue) => {
            const countryCode = "+" + countryData.dialCode;
            const finalMobileNum = formatedValue
              ? formatedValue?.split(countryCode)[1]?.trim()
              : "";
            formik.setValues((v) => ({
              ...v,
              customer_info: {
                ...formik.values.customer_info,
                customer_alt_phone: finalMobileNum,
                customer_alt_phone_country_code: countryCode,
              },
            }));
          }}
          disabled={active !== 0}
        />
      </div>

      {/* Business Account */}
      <div className="my-2 col-md-4">
        <div className="form-group bmd-form-group is-filled text-left">
          <label className="bmd-label-floating">Business Account</label>
          <SwitchBox
            value={formik.values?.customer_info?.is_business_account}
            onChange={(data) => {
              formik.setValues((e) => ({
                ...e,
                customer_info: {
                  ...formik.values?.customer_info,
                  is_business_account: data ? 1 : 0,
                },
              }));
            }}
          />
        </div>
      </div>

      {/* Module-specific checkboxes */}
      {module === "edit-order" && (
        <div className="col-12">
          <FormControlLabel
            control={
              <GreenCheckbox
                checked={formik.values.verified}
                onChange={() => {
                  formik.setValues((v) => ({
                    ...v,
                    verified: !v.verified,
                    keep_processing: false,
                  }));
                }}
              />
            }
            label="Update and Process Order"
          />

          {orderDetail?.order_status === PROCESSING_STATE && (
            <FormControlLabel
              control={
                <GreenCheckbox
                  checked={formik.values.keep_processing}
                  onChange={() => {
                    formik.setValues((v) => ({
                      ...v,
                      verified: false,
                      keep_processing: !v.keep_processing,
                    }));
                  }}
                />
              }
              label="Update and Keep in Process"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerInfo;
